import { useEffect, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Button, Textarea, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from '../../hooks/useTranslation';

/** Shared draft editor: the caller owns saving and the shipment context. */
export default function LogbookNoteEditor({
  note,
  onNoteChange,
  photos,
  onPhotosChange,
  disabled = false,
  placeholder,
  optional = false,
  compact = false,
}: {
  note: string;
  onNoteChange: (value: string) => void;
  photos: File[];
  onPhotosChange: Dispatch<SetStateAction<File[]>>;
  disabled?: boolean;
  placeholder?: string;
  optional?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation('market');
  const input = useRef<HTMLInputElement>(null);
  const previews = useMemo(
    () => photos.map((file) => URL.createObjectURL(file)),
    [photos]
  );
  useEffect(
    () => () => previews.forEach((url) => URL.revokeObjectURL(url)),
    [previews]
  );
  return (
    <div className='flex flex-col gap-3'>
      {optional && <p className='text-sm text-default-500'>{t('Opcional')}</p>}
      <div
        className={compact ? 'flex items-start rounded-medium bg-default-100' : ''}
      >
        <Textarea
          label={t('Comentario')}
          placeholder={
            placeholder ||
            t('Cómo estaba la fruta, cómo se movió el mercado...')
          }
          value={note}
          onValueChange={onNoteChange}
          minRows={compact ? 1 : 3}
          maxRows={compact ? 6 : undefined}
          classNames={
            compact
              ? { label: 'sr-only', inputWrapper: 'bg-transparent shadow-none' }
              : undefined
          }
          isDisabled={disabled}
        />
        {compact && (
          <Tooltip content={t('Foto')}>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              className='m-2 shrink-0'
              aria-label={t('Foto')}
              isDisabled={disabled}
              onPress={() => input.current?.click()}
            >
              <Icon icon='tabler:camera' width={18} />
            </Button>
          </Tooltip>
        )}
      </div>
      <input
        ref={input}
        type='file'
        accept='image/*'
        capture='environment'
        multiple
        className='hidden'
        disabled={disabled}
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) onPhotosChange((current) => [...current, ...files]);
          event.target.value = '';
        }}
      />
      <div className='flex flex-wrap items-center gap-2'>
        {photos.map((file, index) => (
          <div
            key={`${file.name}-${index}`}
            className='relative h-20 w-20 overflow-hidden rounded-medium border border-default-200'
          >
            <img
              src={previews[index]}
              alt={file.name}
              className='h-full w-full object-cover'
            />
            <div className='absolute right-0 top-0 rounded-full bg-content1'>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label={t('Quitar foto')}
                isDisabled={disabled}
                className='h-6 min-w-6'
                onPress={() =>
                  onPhotosChange((current) =>
                    current.filter((_, i) => i !== index)
                  )
                }
              >
                <Icon icon='tabler:x' width={14} />
              </Button>
            </div>
          </div>
        ))}
        {!compact && (
          <Button
            variant='flat'
            className='h-20 min-w-20 flex-col gap-1 text-xs'
            isDisabled={disabled}
            onPress={() => input.current?.click()}
          >
            <Icon icon='tabler:camera' width={24} />
            {t('Foto')}
          </Button>
        )}
      </div>
    </div>
  );
}
